import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService } from '../../../../services/base/authentication.service';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Report } from '../../../../interfaces/report/report';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Organisation } from '../../../../interfaces/routering/organisation';
import { Subject, Subscription } from 'rxjs';
import { FiltersService } from '../../../../services/base/filters.service';
import { Filters } from '../../../../interfaces/base/filters';
import * as moment from 'moment';
import { ReportService } from '../../../../services/reports/report.service';

@Component({
  selector: 'fury-other-apps-dialog',
  templateUrl: './other-apps-dialog.component.html',
  styleUrls: ['./other-apps-dialog.component.scss'],
})

export class OtherAppsDialogComponent implements OnInit, OnDestroy {

  public filters: Filters = {
    'reportNumbers': {
      preload: false,
      isLoading: false,
      data: <Report[]>[],
    },
  };

  public form = this.fb.group({});

  public reportNumberAbbreviation: string = 'SO';
  public suggestReportNumbers: Report[] = null;
  public years: number[] = [];
  public yearSelection: FormControl = new FormControl<number>(null);

  private reportNumberSubscription$: Subscription = null;
  private destroy$: Subject<null> = new Subject<null>();

  constructor(
    private filtersService: FiltersService,
    private fb: FormBuilder,
    private reportService: ReportService,
    public authenticationService: AuthenticationService,
  ) {
  }

  ngOnInit(): void {
    this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((data: Organisation): void => {
      if (data) {
        this.reportNumberAbbreviation = data.name;

        if (data.domains) {
          data.domains.forEach((d) => {
            this.form.addControl(d.type.name, new FormGroup({
              report_type: new FormControl(<string>null),
              report_number: new FormControl(<string>null),
              report_unique_number: new FormControl(<string>null),
              isLoading: new FormControl(<boolean>false),
            }));

            if (['track-and-trace', 'survey'].includes(d.type.name)) {
              this.form.controls[d.type.name].controls.report_type.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string): void => {
                if (value === 'last') {
                  this.form.controls[d.type.name].controls.isLoading.setValue(true);
                  this.reportService.getLastReport().subscribe((report: Report): void => {
                    if (report) {
                      this.form.controls[d.type.name].patchValue({
                        report_unique_number: report.unique_number,
                      }, {emitEvent: false});
                    }
                    this.form.controls[d.type.name].controls.isLoading.setValue(false);
                  });
                } else if (value === 'random') {
                  this.form.controls[d.type.name].controls.isLoading.setValue(true);
                  this.reportService.getRandomReport().subscribe((report: Report): void => {
                    if (report) {
                      this.form.controls[d.type.name].patchValue({
                        report_unique_number: report.unique_number,
                      }, {emitEvent: false});
                    }
                    this.form.controls[d.type.name].controls.isLoading.setValue(false);
                  });
                } else {
                  this.form.controls[d.type.name].controls.report_number.valueChanges.pipe(
                    debounceTime(250),
                    distinctUntilChanged(),
                    filter((q: string | object): boolean | null => (typeof q === 'string' ? q && q !== '' : typeof q === 'object')),
                    takeUntil(this.destroy$)
                  ).subscribe((value2: string | Report): void => {
                    this.form.controls[d.type.name].controls.isLoading.setValue(true);
                    this.suggestReportNumbers = [];
                    if (value2) {
                      if (typeof value2 === 'string') {
                        this.filters.reportNumbers.isLoading = true;

                        if (this.reportNumberSubscription$ !== null) {
                          this.reportNumberSubscription$.unsubscribe();
                        }
                        this.reportNumberSubscription$ = this.filtersService.reportNumbers(this.reportNumberAbbreviation + this.yearSelection.value + '-' + value2)
                          .pipe(takeUntil(this.destroy$)).subscribe((data2: Report[]): void => {
                              if (data2) {
                                this.suggestReportNumbers = <Report[]>data2;
                                this.filters.reportNumbers.isLoading = false;
                              }
                              this.form.controls[d.type.name].controls.isLoading.setValue(false);
                            },
                            (): boolean => this.filters.reportNumbers.isLoading = false
                          );
                      } else {
                        this.form.controls[d.type.name].patchValue({
                          report_unique_number: value2.unique_number,
                        }, {emitEvent: false});
                        this.form.controls[d.type.name].controls.isLoading.setValue(false);
                      }
                    }
                  });
                }
              });
            }
          });
        }
      }
    });

    const startDate: Date = new Date('January 1, 2018 00:00:00'),
      endDate: Date = new Date(),
      years: number = moment(endDate).diff(startDate, 'years');
    for (let year: number = 0; year <= years; year++) {
      this.years.push(parseInt((startDate.getFullYear() + year).toString().slice(1), 10));
    }

    this.yearSelection.setValue(this.years.slice(this.years.length - 1)[0], {emitEvent: false});
  }

  formatReportNumberDropdown(result: any): string {
    return result !== null && typeof result === 'object' ? result.number.slice(5) : result;
  }

  getOptionUrl(url: string, option: string): string {
    const element: string = this.form.controls[option]?.controls?.report_unique_number?.value;

    if (element !== undefined && typeof element === 'string') {
      return url + '/' + element;
    }

    return url;
  }

  log(): void {
    console.log(this.form);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
